import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Actions from '../actions/Actions';
import styles from './Slide.module.scss';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Spinner from '@components/spinner/Spinner';
import Button from '@components/button/Button';
import { getMediaAbsolutePath } from '@services/File';
import { useCourseContent } from '@src/hooks/useCourseContent';
import { isHomeEDApp } from '@src/constants';
// Use singleton worker to prevent destruction by React StrictMode
import pdfWorkerSingleton from '../../../../utils/pdfWorkerSingleton';

type Props = {
  data: string;
  isFullScreen: boolean;
  isPreview?: boolean;
  goBack?: () => void;
  pageNumber: number;
  changePageNumber: (value: number) => void
  startLesson?: () => void;
};

function Slide(props: Props) {
  const { data, isFullScreen, isPreview, goBack, pageNumber, changePageNumber, startLesson } = props;
  

  const {
    content, totalPages, setTotalPages, accountLocked,
    completeModal, setCompleteModal, isNewLessonLayout, requestSilentMarkComplete,
  } = useCourseContent();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const autoCompleteTimerRef = useRef<number | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    setTotalPages(1);
  }, [data, setTotalPages]);

  useEffect(() => {
    return () => {
      if (autoCompleteTimerRef.current != null) {
        window.clearTimeout(autoCompleteTimerRef.current);
        autoCompleteTimerRef.current = null;
      }
    };
  }, []);

  const fileObj = useMemo(() => {
    return getMediaAbsolutePath(data);
  }, [data]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setTotalPages(numPages);
    if (content?.lastInLesson && (!isHomeEDApp || !accountLocked) && (!isPreview || isNewLessonLayout) && !completeModal) {
      if (autoCompleteTimerRef.current != null) {
        window.clearTimeout(autoCompleteTimerRef.current);
      }
      autoCompleteTimerRef.current = window.setTimeout(() => {
        autoCompleteTimerRef.current = null;
        requestSilentMarkComplete();
      }, 4500);
    }
  }, [setTotalPages, content?.lastInLesson, accountLocked, isPreview, isNewLessonLayout, completeModal, requestSilentMarkComplete ]);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50); 
    }
  }, [calculateSize, width]);

  const prevDisabled = useMemo(() => pageNumber <= 1, [pageNumber]);
  const isLast = useMemo(() => pageNumber >= totalPages, [pageNumber, totalPages]);

  const handleLeft = useCallback(() => {
    changePageNumber(Math.max(pageNumber - 1, 1));
  }, [pageNumber, changePageNumber]);

  const handleRight = useCallback(() => {
    changePageNumber(Math.min(pageNumber + 1, totalPages));
  }, [totalPages, pageNumber, changePageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return <Button size={'extraSmall'} onClick={handleLeft} className={styles.previousBtn} label={'PREVIOUS'}/>;
  }, [prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={'extraSmall'} onClick={handleRight} label={'NEXT'}/>;
  }, [isLast, handleRight]);

  const ratio = useMemo(() => {
    if (width && height) {
      return width/height
    }
    return 1
  }, [width, height])

  if (!fileObj) return null;

  return (
    <Actions fullScreen={isFullScreen} leftButton={prevButton} rightButton={nextButton} totalPages={totalPages} pageNumber={pageNumber} isPreview={isPreview} goBack={goBack}>
      <div className={`${styles.wrapper} ${!isPreview ? styles.blackfullScreen : ''}`} ref={wrapperRef}>
        <div className={isFullScreen ? styles.fullContent : styles.content}>
          <Document
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            className={isPreview ? styles.previewDocument : styles.document}
            loading={<div className={styles.loading}><Spinner/></div>}
          >
            <Page loading={<div className={styles.loading}><Spinner/></div>} pageNumber={pageNumber} width={ratio > 1.7 ? 0 : width} 
                  height={height} className={isPreview && !isHomeEDApp ? styles.roundCanvas : ''}/>
          </Document>
          {(isPreview && !isHomeEDApp && !isNewLessonLayout) ? <div className={styles.previewBeginLesson}>
            <div className={styles.pulse}><Button label={'Begin Lesson'} color='white' className={styles.beginLesson} onClick={() => startLesson && startLesson()} /></div>
          </div> : null}
        </div>
      </div>
    </Actions>
  );
}

export default Slide;
